/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { SessionSignalsStateService } from './application/session/session-store.service';
import { IToastController } from './domain/global/i-toast-controller';
import { ISessionRepository } from './domain/session/i-session-repository';
import { rightValue } from './util/either';
import { isUnauthenticatedError } from './util/http-error';

let refreshTokenPromise: Promise<string> | null = null;
let isSessionExpired = false;

export function resetAuthInterceptorState(): void {
  refreshTokenPromise = null;
  isSessionExpired = false;
}

const isAdAuthRequest = (url: string): boolean => url.includes('/ad-auth/');

export const httpInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const sessionRepository = inject(ISessionRepository);
  const sessionStore = inject(SessionSignalsStateService);
  const toastController = inject(IToastController);

  const expireSession = (): void => {
    if (!isSessionExpired) {
      toastController.showErrorMessage('Sua sessão expirou. Entre novamente.');
    }

    isSessionExpired = true;
  };

  if (isSessionExpired && !isAdAuthRequest(request.url)) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: 'Sessão expirada',
          url: request.url,
        })
    );
  }

  const refreshToken = async (): Promise<string> => {
    const pending =
      refreshTokenPromise ??
      sessionRepository.refreshToken().then(result => {
        if (result.isLeft()) {
          throw new Error('Falha ao renovar o token');
        }

        return rightValue(result);
      });

    refreshTokenPromise = pending;

    try {
      const token = await pending;

      if (refreshTokenPromise === pending) {
        refreshTokenPromise = null;
      }

      return token;
    } catch (error) {
      if (refreshTokenPromise === pending) {
        refreshTokenPromise = null;
      }

      expireSession();
      await sessionStore.logout();
      throw error;
    }
  };

  const retryWithFreshToken = async (): Promise<HttpRequest<any>> => {
    const token = await refreshToken();
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  };

  return next(request).pipe(
    catchError((error: unknown) => {
      const isUnauthorized = isUnauthenticatedError(error);

      if (!isUnauthorized || isAdAuthRequest(request.url) || isSessionExpired) {
        return throwError(() => error);
      }

      return from(retryWithFreshToken()).pipe(
        switchMap(retriedRequest =>
          next(retriedRequest).pipe(
            catchError(retryError => {
              if (isUnauthenticatedError(retryError)) {
                expireSession();
                refreshTokenPromise = null;

                return from(sessionStore.logout()).pipe(
                  switchMap(() => throwError(() => retryError))
                );
              }

              return throwError(() => retryError);
            })
          )
        )
      );
    })
  );
};
